<script setup lang="ts">
    import { ref, computed } from 'vue'
    import { useRoute } from 'vue-router'

    import { strCompare } from '@common/Common'
    import { dynamicUiRoutes } from '@common/dynamicUiRoutes'

    import { ScoringPanelistsManagerViewModel } from '@Evaluation/AlternativesSurveys/Components/ScoringPanelistsManager'

    /* ---------------- props / emits ---------------- */

    const props = defineProps<{
        viewModel: ScoringPanelistsManagerViewModel
    }>()

    const emit = defineEmits<{
        (e: 'include-panelist', projectUserId: number): void
    }>()

    /* ---------------- router ---------------- */

    const route = useRoute()

    /* ---------------- shortcuts ---------------- */

    const vm = computed(() => props.viewModel)

    const opened = computed(() => vm.value.opened)
    const workspaceId = computed(() => vm.value.workspaceId)
    const projectId = computed(() => vm.value.projectId)

    const initialized = computed(() => vm.value.data != null)
    const projectUsers = computed(() => vm.value.data?.ProjectUsers ?? [])
    const tags = computed(() => vm.value.data?.ProjectUsersTags ?? [])

    /* ---------------- urls ---------------- */

    const url_PeoplePage = computed(() =>
        dynamicUiRoutes.ProjectUsers_PeoplePage(
            route,
            workspaceId.value,
            projectId.value
        )
    )

    /* ---------------- local state ---------------- */

    const tag = ref<string | null>(null)
    const searchPhrase = ref<string | null>(null)

    /* ---------------- derived ---------------- */

    const projectUsersView = computed(() =>
    {
        if (!projectUsers.value)
        {
            return null
        }

        let view = projectUsers.value.filter(u => u.IsArchived === false)

        if (searchPhrase.value)
        {
            const phrase = searchPhrase.value.toLowerCase()
            view = view.filter(u =>
                `${u.Name} ${u.Email}`.toLowerCase().includes(phrase)
            )
        }

        if (tag.value)
        {
            view = view.filter(u => u.Tags.includes(tag.value))
        }

        return [...view].sort((a, b) =>
            strCompare(a.Name.toLowerCase(), b.Name.toLowerCase())
        )
    })

    /* ---------------- actions ---------------- */

    function handleIncludePanelist(projectUserId: number)
    {
        emit('include-panelist', projectUserId)
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">Add panelists</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body" style="min-height: 300px;">
                        <h3 v-if="!initialized"><i class="fa fa-spinner fa-spin"></i> Loading...</h3>

                        <div v-if="initialized">

                            <div v-if="projectUsers.length === 0">
                                <p class="lead">
                                    You currently have no Respondents set-up in the software.
                                    Please go to the <a class="text-primary" v-sanitizedHref="url_PeoplePage" target="_blank">People section</a> to upload names & emails.
                                </p>
                            </div>

                            <div v-if="projectUsers.length > 0">

                                <nav class="navbar navbar-light navbar-expand-md bg-light" role="navigation">
                                    <form class="form-inline my-2 my-lg-0 ml-auto" role="search">
                                        <input class="form-control form-control-sm mr-sm-2" type="search" placeholder="Search" v-model="searchPhrase">
                                    </form>
                                    <form class="form-inline my-2 my-lg-0 ">
                                        <div class="dropdown">
                                            <button class="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-toggle="dropdown">
                                                <span>Filter by: {{tag ?? "Show all"}}</span>
                                            </button>
                                            <div class="dropdown-menu dropdown-menu-right">
                                                <a class="dropdown-item" @click="tag = null">Show all</a>
                                                <h6 class="dropdown-header">By tag:</h6>
                                                <a class="dropdown-item" v-for="t in tags" @click="tag = t">{{t}}</a>
                                            </div>
                                        </div>
                                    </form>
                                </nav>

                                <table class="table">
                                    <tbody>
                                        <tr v-for="pu in projectUsersView" :key="pu.ProjectUserId">
                                            <td>
                                                <p class="fg-color-gray mb-0 mt-1">
                                                    <span>{{pu.Name}}</span>
                                                </p>
                                                <small class="text-muted" v-if="pu.HasEmail">{{pu.Email}}</small>
                                                <small class="text-danger" v-if="!pu.HasEmail">Email not provided</small>
                                            </td>
                                            <td>
                                                <span class="badge badge-secondary mr-1" v-for="t in pu.Tags" :key="t">{{t}}</span>
                                            </td>
                                            <td style="width: 80px;">
                                                <button type="button" class="btn btn-outline-secondary" @click="handleIncludePanelist(pu.ProjectUserId)" :disabled="pu.Included">
                                                    {{pu.Included ? "Added" : "Add"}}
                                                </button>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <a class="btn btn-outline-secondary" v-sanitizedHref="url_PeoplePage" target="_blank">Add people</a>
                        <button @click="close" type="button" class="btn btn-outline-secondary">Close</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>